import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { CallContext } from "./api/models";
import { useChannelState } from "./channelContext";
import { ChannelMapToShort } from "./channelUtil";
import { subSeconds } from "date-fns";

const LISTENING_THRESHOLD_SEC = 15;

export type DialerPullContext = {
  initialListeningTime: Date | null;
  callConnected: boolean;
  callContext: CallContext | null;
  setCallContext: (cc: CallContext | null) => void;
  shouldDialerPull: boolean;
  setShouldDialerPull: (b: boolean) => void;
};

const DialerPullContext = createContext<DialerPullContext | undefined>(
  undefined,
);

function _useDialerPullState(): DialerPullContext {
  const [initialListeningTime, setInitialListeningTime] = useState<Date | null>(
    null,
  );
  const [callContext, setCallContext] = useState<CallContext | null>(null);
  const [shouldDialerPull, setShouldDialerPull] = useState<boolean>(false);

  const callConnected = useMemo(
    () => !!callContext && callContext.callId !== "0",
    [callContext],
  );

  const { setChannel } = useChannelState();

  const setCallContextCallback = useCallback(
    (cc: CallContext | null) => {
      if (cc) {
        setShouldDialerPull(true);
        setInitialListeningTime((v) =>
          v ? v : subSeconds(new Date(), LISTENING_THRESHOLD_SEC),
        );
        setChannel(ChannelMapToShort[cc.channel]);
      }
      setCallContext(cc);
    },
    [setChannel],
  );

  const setShouldDialerPullCallback = useCallback((b: boolean) => {
    if (!b) {
      setCallContext(null);
    }
    setInitialListeningTime((v) =>
      b ? (v ? v : subSeconds(new Date(), LISTENING_THRESHOLD_SEC)) : null,
    );
    setShouldDialerPull(b);
  }, []);

  return {
    initialListeningTime,
    callConnected,
    shouldDialerPull,
    setShouldDialerPull: setShouldDialerPullCallback,
    callContext,
    setCallContext: setCallContextCallback,
  };
}

export function useDialerPullState() {
  const context = useContext(DialerPullContext);
  if (context) return context;
  return _useDialerPullState();
}

export function Provider({ children }: { children: React.ReactNode }) {
  const state = useDialerPullState();

  return createElement(DialerPullContext.Provider, { value: state }, children);
}
